import type { ReactNode } from 'react';
import { ThemeEmoji } from './ThemeEmoji';

export interface PillProps {
  children: ReactNode;
  bg: string;
  fg: string;
  className?: string;
  icon?: ReactNode;
  dot?: string;
  /**
   * MKS-493 — render the Sparkle "joy / done" emoji after the label. Set it
   * on approved/done chips only; it is invisible in every other theme. Never
   * set it on an error, a warning or a numeric cell.
   */
  done?: boolean;
}

/**
 * Pill — inline-flex semantic pill primitive.
 *
 * Per Phase 41 PATTERNS.md anti-pattern: semantic colors flow via INLINE
 * `style` (data-as-style), NOT via Tailwind classes. The structural shape
 * (rounded-full, inline-flex, type ramp) stays in the className.
 *
 * Source: .planning/v7-design-handoff/v7-sla/shell.jsx lines 10-17
 */
export function Pill({ children, bg, fg, className = '', icon, dot, done }: PillProps) {
  return (
    // MKS-493 — the `ms-pill` hook carries Sparkle's 12px/600 chip type.
    // `rounded-full` is kept verbatim: a pill is already a pill in both themes.
    <span
      className={`ms-pill inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold leading-none ${className}`}
      style={{ background: bg, color: fg }}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
      {icon}
      {children}
      {done && <ThemeEmoji role="done" />}
    </span>
  );
}
